import { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    getRolloutReadiness,
    ReadinessState,
    RolloutReadiness,
    saveRolloutPlan,
} from '../../api/rollout';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { ago, span } from './rolloutModel';
import { Card, StateIcon } from './rolloutParts';

const VERDICT: Record<ReadinessState, (n: number) => string> = {
    Ok: () => 'Go: niets houdt de deploy tegen',
    Warn: (n) => `Let op: ${n} ${n === 1 ? 'punt' : 'punten'} om na te lopen`,
    Bad: (n) =>
        `Niet deployen: ${n} ${
            n === 1 ? 'punt houdt' : 'punten houden'
        } de deploy tegen`,
    Unknown: (n) => `Let op: ${n} ${n === 1 ? 'punt' : 'punten'} niet gelezen`,
};

const TONE: Record<ReadinessState, 'ok' | 'warn' | 'bad'> = {
    Ok: 'ok',
    Warn: 'warn',
    Bad: 'bad',
    Unknown: 'warn',
};

/**
 * Before a deploy: the checks from the rollout runbook that can be read, where to go back to,
 * and the deploy card of the environment.
 */
export const ReadinessPanel = ({
    customer,
    environment,
    now,
}: {
    customer: string;
    environment: string;
    now: number;
}) => {
    const axios = useAxiosSwagger();
    const [readiness, setReadiness] = useState<RolloutReadiness>();
    const [error, setError] = useState<string>();
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState<string>();
    const [rollbackRun, setRollbackRun] = useState('');
    const [note, setNote] = useState('');

    const load = useCallback(async () => {
        if (!axios) return;

        setBusy(true);
        setError(undefined);

        try {
            const next = await getRolloutReadiness(
                axios,
                customer,
                environment
            );
            setReadiness(next);
            setRollbackRun(
                next.plan?.rollbackRun ??
                    next.card.runningBuildNumber ??
                    next.card.runningCommit ??
                    ''
            );
            setNote(next.plan?.note ?? '');
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    }, [axios, customer, environment]);

    useEffect(() => {
        load();
    }, [load]);

    const save = async (signOff: boolean) => {
        if (!axios) return;

        await saveRolloutPlan(axios, customer, environment, {
            rollbackRun,
            note,
            signOff,
        });
        setSaved(signOff ? 'Go vastgelegd.' : 'Opgeslagen.');
        await load();
    };

    if (!readiness) {
        return (
            <section className="ro-card">
                <div className="ro-empty" aria-busy={!error}>
                    {error ? (
                        <span className="ro-error">{error}</span>
                    ) : (
                        <>
                            <FontAwesomeIcon icon="spinner" spin /> Go/no-go
                            ophalen
                        </>
                    )}
                </div>
            </section>
        );
    }

    const open = readiness.checks.filter((c) => c.state !== 'Ok').length;
    const card = readiness.card;
    const running = card.runningBuildNumber ?? card.runningCommit;
    const runningBoth = [card.runningBuildNumber, card.runningCommit]
        .filter(Boolean)
        .join(' · ');

    return (
        <>
            <section className={`ro-card ro-card--${TONE[readiness.verdict]}`}>
                <div className="ro-verdict">
                    <div className="ro-verdict__text">
                        <StateIcon state={readiness.verdict} />
                        <strong>{VERDICT[readiness.verdict](open)}</strong>
                        <span className="ro-muted">
                            gecontroleerd{' '}
                            {span(now - Date.parse(readiness.checkedAt))}{' '}
                            geleden
                        </span>
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={load}
                        disabled={busy}
                    >
                        {busy ? (
                            <>
                                <FontAwesomeIcon icon="spinner" spin /> Go/no-go
                                ophalen
                            </>
                        ) : (
                            'Opnieuw controleren'
                        )}
                    </button>
                </div>
            </section>

            <div className="ro-grid">
                <div className="ro-grid__col">
                    <Card title="Controles" aside="uit het uitrolrunbook">
                        <div className="ro-scroll">
                            <table className="ro-table">
                                <thead>
                                    <tr>
                                        <th
                                            className="is-state"
                                            aria-label="Status"
                                        />
                                        <th>Controle</th>
                                        <th>Toelichting</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {readiness.checks.map((c) => (
                                        <tr
                                            key={c.key}
                                            className={
                                                c.state === 'Bad'
                                                    ? 'is-attention'
                                                    : undefined
                                            }
                                        >
                                            <td className="is-state">
                                                <StateIcon state={c.state} />
                                            </td>
                                            <td>{c.label}</td>
                                            <td className="ro-muted is-wrap">
                                                {c.detail}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </div>

                <div className="ro-grid__col">
                    <Card title="Terugzetpunt en go">
                        <div className="ro-field">
                            <label htmlFor="ro-rollback">Terugzetpunt</label>
                            <input
                                id="ro-rollback"
                                value={rollbackRun}
                                onChange={(e) => setRollbackRun(e.target.value)}
                                placeholder="bijv. 20261029.3"
                            />
                            <span
                                className="ro-field__help"
                                title="Eerste uitrol: de laatste run van de oude pipeline (Azure DevOps → Environments → Deployments)"
                            >
                                Draait nu: {running ?? '–'} · eerste uitrol:
                                laatste run van de oude pipeline
                            </span>
                        </div>
                        <div className="ro-field">
                            <label htmlFor="ro-note">Notitie</label>
                            <input
                                id="ro-note"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="bijv. na de receivables-run van 14:00"
                            />
                        </div>
                        <div className="ro-actions">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => save(false)}
                            >
                                Opslaan
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => save(true)}
                                disabled={readiness.verdict === 'Bad'}
                                title={
                                    readiness.verdict === 'Bad'
                                        ? 'Eerst de rode punten oplossen'
                                        : 'Legt vast dat jij de deploy goedkeurt'
                                }
                            >
                                Go geven
                            </button>
                            {saved && <span className="ro-muted">{saved}</span>}
                        </div>
                        {readiness.plan?.signedOffBy && (
                            <p className="ro-card__foot">
                                Go gegeven door{' '}
                                <strong>{readiness.plan.signedOffBy}</strong>,{' '}
                                {ago(readiness.plan.signedOffAt, now)}.
                            </p>
                        )}
                    </Card>

                    <Card title="Deploykaart">
                        <dl className="ro-dl">
                            <dt>Groep</dt>
                            <dd>{card.group}</dd>
                            <dt>Receivables</dt>
                            <dd>
                                {card.receivables == null
                                    ? 'onbekend'
                                    : card.receivables
                                    ? 'aan'
                                    : 'uit'}
                                {card.receivables &&
                                    card.watermarkAdvancedAt &&
                                    `, watermark ${ago(
                                        card.watermarkAdvancedAt,
                                        now
                                    )} opgeschoven`}
                            </dd>
                            <dt>Draait nu</dt>
                            <dd className="ro-mono">{runningBoth || '–'}</dd>
                            <dt>Tijdzone</dt>
                            <dd className="ro-muted">
                                niet leesbaar (TimezoneImpact)
                            </dd>
                            <dt>Features aan</dt>
                            <dd>
                                <div className="ro-chips">
                                    {card.featuresOn.map((f) => (
                                        <span key={f}>{f}</span>
                                    ))}
                                </div>
                            </dd>
                            <dt>Settings</dt>
                            <dd className="ro-muted">
                                {card.settingNames.length} ingesteld
                            </dd>
                        </dl>
                    </Card>
                </div>
            </div>
        </>
    );
};
